import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Phone, Mail } from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import useSEO from '../hooks/useSEO';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PaymentResult = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useApp();
  const ref = params.get('ref') || '';
  const [status, setStatus] = useState('pending');
  useSEO('Payment | CAB.TAXI', 'CAB.TAXI');

  const T = {
    checking: { de: 'Zahlung wird überprüft…', en: 'Verifying payment…', fr: 'Vérification du paiement…', it: 'Verifica del pagamento…' }[lang],
    paidTitle: { de: 'Zahlung erhalten!', en: 'Payment received!', fr: 'Paiement reçu !', it: 'Pagamento ricevuto!' }[lang],
    paidMsg: { de: 'Vielen Dank! Ihre Buchung ist bezahlt. Nach der Prüfung erhalten Sie eine Bestätigung.', en: 'Thank you! Your booking is paid. After review you will receive a confirmation.', fr: 'Merci ! Votre réservation est payée. Après vérification, vous recevrez une confirmation.', it: 'Grazie! La tua prenotazione è pagata. Dopo la verifica riceverai una conferma.' }[lang],
    failTitle: { de: 'Zahlung nicht abgeschlossen', en: 'Payment not completed', fr: 'Paiement non finalisé', it: 'Pagamento non completato' }[lang],
    failMsg: { de: 'Die Zahlung wurde nicht abgeschlossen. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.', en: 'The payment was not completed. Please try again or contact us.', fr: 'Le paiement n\'a pas abouti. Réessayez ou contactez-nous.', it: 'Il pagamento non è stato completato. Riprova o contattaci.' }[lang],
    reference: { de: 'Referenz', en: 'Reference', fr: 'Référence', it: 'Riferimento' }[lang],
    home: { de: 'Zurück zur Startseite', en: 'Back to home', fr: 'Retour à l\'accueil', it: 'Torna alla home' }[lang],
    retry: { de: 'Erneut buchen', en: 'Book again', fr: 'Réserver à nouveau', it: 'Prenota di nuovo' }[lang],
  };

  useEffect(() => {
    if (!ref) { setStatus('failed'); return; }
    let stop = false; let tries = 0;
    const poll = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/payments/status/${ref}`);
        const d = await r.json();
        if (stop) return;
        setStatus(d.payment_status);
        tries += 1;
        if (d.payment_status === 'pending' && tries < 20) setTimeout(poll, 3000);
      } catch (e) {
        if (!stop && tries < 20) { tries += 1; setTimeout(poll, 3000); }
      }
    };
    poll();
    return () => { stop = true; };
  }, [ref]);

  const paid = status === 'paid';
  const failed = status === 'failed';
  const pending = !paid && !failed;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="payment-result">
        <div className="rounded-3xl bg-white border border-gray-100 shadow-sm p-8 sm:p-12 text-center">
          {pending && (
            <>
              <Loader2 className="w-12 h-12 mx-auto mb-6 animate-spin" style={{ color: 'var(--brand-accent)' }} />
              <h1 className="text-xl font-extrabold text-[color:var(--brand-dark)]" data-testid="payment-status-pending">{T.checking}</h1>
            </>
          )}
          {paid && (
            <>
              <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(22,163,74,0.12)' }}>
                <CheckCircle2 className="w-11 h-11" style={{ color: '#16a34a' }} />
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[color:var(--brand-dark)]" data-testid="payment-status-paid">{T.paidTitle}</h1>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed">{T.paidMsg}</p>
            </>
          )}
          {failed && (
            <>
              <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(239,68,68,0.12)' }}>
                <XCircle className="w-11 h-11" style={{ color: '#ef4444' }} />
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[color:var(--brand-dark)]" data-testid="payment-status-failed">{T.failTitle}</h1>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed">{T.failMsg}</p>
            </>
          )}

          {ref && (
            <div className="mt-6 inline-flex flex-col items-center gap-1 rounded-2xl bg-[color:var(--brand-muted)] px-8 py-4">
              <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">{T.reference}</span>
              <span className="text-xl font-extrabold text-[color:var(--brand-dark)]">{ref}</span>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm">
            <a href="tel:+41791772424" className="inline-flex items-center gap-2 font-bold text-[color:var(--brand-dark)] hover:text-[color:var(--brand-accent)]"><Phone className="w-4 h-4" /> +41 79 177 24 24</a>
            <a href="mailto:info@cab.taxi" className="inline-flex items-center gap-2 font-bold text-[color:var(--brand-dark)] hover:text-[color:var(--brand-accent)]"><Mail className="w-4 h-4" /> info@cab.taxi</a>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => navigate('/')} className="accent-btn rounded-xl px-6 py-3 font-bold" data-testid="payment-home">{T.home}</button>
            {failed && <button onClick={() => navigate('/booking')} className="rounded-xl px-6 py-3 font-bold border-2 border-[color:var(--brand-dark)] text-[color:var(--brand-dark)] hover:bg-[color:var(--brand-dark)] hover:text-white transition-colors" data-testid="payment-retry">{T.retry}</button>}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentResult;
